<script lang="ts">
	import type { DateValue } from '@internationalized/date';
	import type { CalendarEvent as CalendarEventType } from '@/types/calendar';
	import CalendarEvent from '../CalendarEvent.svelte';
	import {
		getMonthWeeks,
		formatDayOfWeek,
		formatDayNumber,
		isInMonth,
		isToday,
		formatDateToString
	} from '../calendar.utils';
	import { cn } from '@/lib/utils';

	interface Props {
		currentDate: DateValue;
		events: CalendarEventType[];
		onEventClick?: (event: CalendarEventType) => void;
		showPopover?: boolean;
	}

	let {
		currentDate,
		events,
		onEventClick = () => {},
		showPopover = true
	}: Props = $props();

	const weeks = $derived(getMonthWeeks(currentDate));
	const weekdays = $derived(weeks[0]?.map((date) => formatDayOfWeek(date)) || []);

	// Helper to check if a day is a weekend
	function isWeekend(date: DateValue): boolean {
		return date.dayOfWeek === 6 || date.dayOfWeek === 7;
	}

	function isSunday(date: DateValue): boolean {
		return date.dayOfWeek === 7;
	}

	// Calculate spanning events for a week
	interface SpanningEvent {
		event: CalendarEventType;
		startCol: number; // 0-6
		span: number; // 1-7
		continuesFromPrev: boolean;
		continuesToNext: boolean;
		row: number; // assigned row for layout
	}

	/**
	 * Get the base lead ID (strips '-extra-hire' suffix)
	 */
	function getBaseEventId(eventId: string): string {
		return eventId.replace('-extra-hire', '');
	}

	function getSpanningEventsForWeek(weekDates: DateValue[]): SpanningEvent[] {
		const weekStart = weekDates[0];
		const weekEnd = weekDates[6];
		const weekStartStr = formatDateToString(weekStart);
		const weekEndStr = formatDateToString(weekEnd);

		const spanningEvents: SpanningEvent[] = [];

		for (const event of events) {
			if (!event.start || !event.end) continue;

			const eventStartStr = event.start.split('T')[0];
			const eventEndStr = event.end.split('T')[0];

			// Check if event overlaps with this week
			if (eventEndStr < weekStartStr || eventStartStr > weekEndStr) continue;

			// Calculate start column (0-6)
			let startCol = 0;
			if (eventStartStr > weekStartStr) {
				// Event starts within this week
				for (let i = 0; i < 7; i++) {
					if (formatDateToString(weekDates[i]) === eventStartStr) {
						startCol = i;
						break;
					}
				}
			}

			// Calculate end column and span
			let endCol = 6;
			if (eventEndStr < weekEndStr) {
				// Event ends within this week
				for (let i = 0; i < 7; i++) {
					if (formatDateToString(weekDates[i]) === eventEndStr) {
						endCol = i;
						break;
					}
				}
			}

			const span = endCol - startCol + 1;
			const continuesFromPrev = eventStartStr < weekStartStr;
			const continuesToNext = eventEndStr > weekEndStr;

			spanningEvents.push({
				event,
				startCol,
				span,
				continuesFromPrev,
				continuesToNext,
				row: 0
			});
		}

		// Sort by backend status order (pipeline position), then by start date within each status group
		spanningEvents.sort((a, b) => {
			// Preserve backend ordering (status pipeline) by comparing original array position
			const aIdx = events.indexOf(a.event);
			const bIdx = events.indexOf(b.event);
			if (aIdx !== bIdx) return aIdx - bIdx;
			// Then by start date
			const aStart = a.event.start?.split('T')[0] || '';
			const bStart = b.event.start?.split('T')[0] || '';
			if (aStart !== bStart) return aStart.localeCompare(bStart);
			// Longer events first for better visual stacking
			return b.span - a.span;
		});

		// Assign rows — sequential to preserve sort order, only sharing rows
		// for linked events (original hire + extra hire for the same lead)
		const baseIdToRow: Record<string, number> = {};
		let nextRow = 0;

		for (const se of spanningEvents) {
			const baseId = getBaseEventId(se.event.id);
			const reservedRow = baseIdToRow[baseId];

			if (reservedRow !== undefined) {
				// Place on the same row as the linked event
				se.row = reservedRow;
			} else {
				// Assign the next sequential row
				se.row = nextRow;
				baseIdToRow[baseId] = nextRow;
				nextRow++;
			}
		}

		return spanningEvents;
	}
</script>

<div class="flex h-full flex-col rounded-lg border border-border/80" style="overflow: clip;">
	<!-- Weekday Header -->
	<div
		class="sticky z-30 grid grid-cols-7 border-b bg-muted"
		style="top: var(--view-sticky-top, 0px)"
	>
		{#each weekdays as day, i (day)}
			<div
				class={cn(
					'px-2 py-2 text-center text-sm font-medium text-muted-foreground',
					i < 6 && 'border-r',
					i >= 5 && 'bg-muted/60'
				)}
			>
				{day}
			</div>
		{/each}
	</div>

	<!-- Calendar Grid -->
	<div class="flex-1">
		{#each weeks as week, weekIndex (weekIndex)}
			{@const spanningEvents = getSpanningEventsForWeek(week)}
			{@const isLastRow = weekIndex === weeks.length - 1}
			{@const maxRow = spanningEvents.reduce((max, se) => Math.max(max, se.row), -1) + 1}

			<div class="relative" style="min-height: {Math.max(100, 32 + maxRow * 30)}px;">
				<!-- Day cells grid -->
				<div class="grid grid-cols-7 absolute inset-0">
					{#each week as date, dayIndex (formatDateToString(date))}
						{@const inMonth = isInMonth(date, currentDate)}
						{@const today = isToday(date)}
						{@const weekend = isWeekend(date)}
						{@const sunday = isSunday(date)}
						{@const isLastColumn = dayIndex === 6}

						<div
							class={cn(
								'p-1',
								!isLastColumn && 'border-r border-border/60',
								!isLastRow && 'border-b border-border/60',
								!inMonth && 'bg-muted/30',
								weekend && inMonth && 'bg-muted/20',
								today && 'bg-accent/5'
							)}
						>
							<!-- Day Number -->
							<div class="text-right pr-1">
								<span
									class={cn(
										'text-sm font-semibold inline-block',
										!inMonth && 'text-muted-foreground/70',
										inMonth && !sunday && 'text-foreground/80',
										inMonth && sunday && 'text-destructive',
										today && 'h-6 min-w-6 text-center rounded-full bg-primary text-primary-foreground font-bold'
									)}
								>
									{formatDayNumber(date)}
								</span>
							</div>
						</div>
					{/each}
				</div>

				<!-- Spanning events layer -->
				<div class="absolute inset-0 pt-8 px-1 pointer-events-none">
					<div class="grid grid-cols-7 gap-x-1">
						{#each spanningEvents as { event, startCol, span, row } (event.id)}
							<div
								class="pointer-events-auto"
								style="grid-column: {startCol + 1} / span {span}; grid-row: {row + 1};"
							>
								<div class="h-6 mb-1 mx-0.5">
									<CalendarEvent
										{event}
										onclick={() => onEventClick(event)}
										{showPopover}
										compact
										class="h-full text-sm truncate"
									/>
								</div>
							</div>
						{/each}
					</div>
				</div>
			</div>
		{/each}
	</div>
</div>